import JSZip from "jszip";
import { saveAs } from "file-saver";
import { renderLogoSVG, type RenderOptions } from "./render";
import { buildQrPaths } from "./qrcode";
import type { LogoState } from "../state/logoStore";

type StyleSlice = Parameters<typeof renderLogoSVG>[0] & {
  customIcons?: LogoState["customIcons"];
  qrText?: string | null;
  iconId?: string | null;
};

async function buildOpts(s: StyleSlice): Promise<RenderOptions> {
  const opts: RenderOptions = { customIcons: s.customIcons };
  if (s.iconId === "__qr__" && s.qrText) {
    try { opts.qrPaths = (await buildQrPaths(s.qrText)).paths; } catch {}
  }
  return opts;
}

/**
 * Replace every <image href="..."/> URL in the SVG string with a base64 data URI.
 * Required because SVGs loaded via blob: URLs can't fetch cross-origin images
 * during rasterization, which silently produces blank PNGs in the brand-kit ZIP.
 */
async function inlineImageHrefs(svg: string): Promise<string> {
  const hrefRe = /\bhref="([^"]+)"/g;
  const imageTagRe = /<image\b[^>]*?\/?>/g;
  // Collect unique URLs from <image> tags.
  const urls = new Set<string>();
  for (const tag of svg.match(imageTagRe) ?? []) {
    const h = hrefRe.exec(tag);
    hrefRe.lastIndex = 0;
    if (h && !h[1].startsWith("data:")) urls.add(h[1]);
  }
  if (urls.size === 0) return svg;
  // Fetch each URL once, convert to data URI.
  const dataMap = new Map<string, string>();
  await Promise.all(
    [...urls].map(async (url) => {
      try {
        const res = await fetch(url);
        if (!res.ok) return;
        const blob = await res.blob();
        const dataUrl = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => resolve(reader.result as string);
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
        dataMap.set(url, dataUrl);
      } catch {
        // Leave URL as-is on failure; rasterization may still succeed if it's truly same-origin.
      }
    })
  );
  // Replace href values in <image> tags only.
  return svg.replace(imageTagRe, (tag) =>
    tag.replace(/(\bhref=")([^"]+)(")/, (_, p1, url, p3) => `${p1}${dataMap.get(url) ?? url}${p3}`)
  );
}

/** Save SVG file. */
export async function exportSVG(s: StyleSlice, filename = "logo.svg") {
  const opts = await buildOpts(s);
  const svg = await inlineImageHrefs(renderLogoSVG(s, { ...opts, width: 1024, height: 1024 }));
  saveAs(new Blob([svg], { type: "image/svg+xml;charset=utf-8" }), filename);
}

/** Rasterize SVG to PNG via OffscreenCanvas / canvas + image. */
export async function exportPNG(s: StyleSlice, size = 1024, filename?: string) {
  const opts = await buildOpts(s);
  const svg = await inlineImageHrefs(renderLogoSVG(s, { ...opts, width: size, height: size }));
  const blob = await rasterize(svg, size, size);
  saveAs(blob, filename ?? `logo-${size}.png`);
}

/** Brand kit ZIP — multi-resolution PNG + SVG + favicon set. */
export async function exportBrandKit(s: StyleSlice, brandName = "logo") {
  const opts = await buildOpts(s);
  const zip = new JSZip();
  const sizes = [128, 256, 512, 1024, 2048];
  for (const size of sizes) {
    const svg = await inlineImageHrefs(renderLogoSVG(s, { ...opts, width: size, height: size }));
    const blob = await rasterize(svg, size, size);
    zip.file(`png/${brandName}-${size}.png`, blob);
  }
  for (const size of [16, 32, 48, 96, 192]) {
    const svg = await inlineImageHrefs(renderLogoSVG(s, { ...opts, width: size, height: size }));
    const blob = await rasterize(svg, size, size);
    zip.file(`favicon/favicon-${size}.png`, blob);
  }
  const portableSvg = await inlineImageHrefs(renderLogoSVG(s, { ...opts, width: 1024, height: 1024 }));
  zip.file(`svg/${brandName}.svg`, portableSvg);
  zip.file(`source/${brandName}.logoman.json`, JSON.stringify(s, null, 2));
  zip.file(
    "README.txt",
    `${brandName} brand kit\nGenerated by www.logoman.pro\n\nContents:\n- png/    — multi-size PNG exports\n- favicon/ — favicon set for web\n- svg/    — vector source (scales infinitely)\n- source/ — www.logoman.pro project file (re-import to edit)\n`
  );
  const out = await zip.generateAsync({ type: "blob" });
  saveAs(out, `${brandName}-brand-kit.zip`);
}

/** Save the source JSON only — re-importable later. */
export function exportProject(s: LogoState, filename = "logo.logoman.json") {
  const blob = new Blob([JSON.stringify(s, null, 2)], { type: "application/json" });
  saveAs(blob, filename);
}

async function rasterize(svg: string, w: number, h: number): Promise<Blob> {
  const url = URL.createObjectURL(new Blob([svg], { type: "image/svg+xml" }));
  try {
    const img = await loadImage(url);
    const canvas = document.createElement("canvas");
    canvas.width = w; canvas.height = h;
    const ctx = canvas.getContext("2d")!;
    ctx.drawImage(img, 0, 0, w, h);
    return await new Promise<Blob>((resolve, reject) => {
      try {
        canvas.toBlob(
          (b) => (b ? resolve(b) : reject(new Error("canvas.toBlob returned null (likely tainted canvas)"))),
          "image/png"
        );
      } catch (err) {
        // Tainted canvas throws SecurityError on toBlob — surface it instead of silently failing.
        reject(err);
      }
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}
